import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// PARTNERS → TASKS. EVERY ROW IS DERIVED; NOTHING HERE IS STORED.
//
// 🚨 THERE IS NO "MARK DONE" AND THERE MUST NOT BE ONE. A row exists because a
// reading has not been taken, came back negative, or is waiting on a person's
// judgement. Closing a row without changing the fact underneath it would hide
// the work rather than do it — and the row would come back on the next load
// anyway, which is the honest behaviour. Every row links to the place the work
// is actually done.
//
// ⚠️ THE PAGE SAYS WHETHER THESE ROWS CURRENTLY BLOCK ANYBODY. Licence
// verification gates activation behind a switch that is off while Doorly clears
// this exact backlog, so "urgent" means "this is what the switch is waiting
// for" rather than "a partner is locked out right now".
// ─────────────────────────────────────────────────────────────────────────────

const KIND_LABEL = {
  manual_verification_needed: 'Needs a manual check',
  automated_negative: 'Register returned a finding',
  disciplinary_review: 'Order needs a decision',
  evidence_awaiting_review: 'Document awaiting review',
  verification_lapsed_payout_pending: 'Verification lapsed, payment pending',
};

const TONE = {
  urgent: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b', label: 'Urgent' },
  high: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e', label: 'High' },
};

function age(d) {
  if (d == null) return 'never checked';
  if (d === 0) return 'today';
  return `${d} day${d === 1 ? '' : 's'}`;
}

export default function AdminPartnerTasks() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [scope, setScope] = useState('all');
  const [loading, setLoading] = useState(true);
  // 🚨 doorly_admin_token. 'adminToken' and 'token' do not exist — a page reading
  // either renders an endless spinner behind a 401, which is what
  // test/adminRouterGuard.test.js caught here.
  const token = localStorage.getItem('doorly_admin_token');

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/partner-tasks?scope=${scope}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d.error) setError(d.error); else { setData(d); setError(''); } })
      .catch(() => setError('Could not load the task list.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(load, [load]);

  const rows = (data && data.rows) || [];

  return (
    <AdminLayout>
      <div style={{ padding: '22px 26px', maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>Tasks</h1>
          {data && data.is_admin && (
            <div style={{ display: 'flex', gap: 6 }}>
              {['all', 'mine'].map((s) => (
                <button key={s} type="button" onClick={() => setScope(s)}
                  style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 7,
                    cursor: 'pointer', border: '1px solid',
                    borderColor: scope === s ? '#111827' : '#d1d5db',
                    background: scope === s ? '#111827' : '#fff',
                    color: scope === s ? '#fff' : '#374151' }}>
                  {s === 'all' ? 'All partners' : 'Mine'}
                </button>
              ))}
            </div>
          )}
        </div>

        <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.6, margin: '6px 0 0' }}>
          Every row here is worked out from the partner&rsquo;s record when this page loads.
          There is nothing to tick off &mdash; do the thing the row describes and it disappears.
        </p>

        {/* 🔑 SAID PLAINLY RATHER THAN IMPLIED BY A COLOUR. */}
        {data && !data.gate_on && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: '#3730a3', background: '#eef2ff',
            border: '1px solid #c7d2fe', borderRadius: 8, padding: '9px 12px', lineHeight: 1.55 }}>
            License verification does not gate activation yet. These items are what that change
            is waiting on &mdash; no partner is currently held by any of them.
          </div>
        )}

        {error && (
          <div style={{ marginTop: 14, fontSize: 12.5, color: '#b91c1c' }}>{error}</div>
        )}
        {loading && !data && (
          <div style={{ marginTop: 18, fontSize: 12.5, color: '#9ca3af' }}>Loading…</div>
        )}

        {data && !rows.length && !loading && (
          <div style={{ marginTop: 18, fontSize: 13, color: '#166534', background: '#f0fdf4',
            border: '1px solid #bbf7d0', borderRadius: 9, padding: '12px 14px' }}>
            Nothing outstanding. Every partner in scope has a current, passing license reading.
          </div>
        )}

        {rows.map((r, i) => {
          const tone = TONE[r.priority] || TONE.high;
          return (
            <div key={`${r.partner_id}-${r.kind}-${r.subject}-${i}`}
              style={{ marginTop: 11, border: `1px solid ${tone.border}`, background: tone.bg,
                borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: '#111827' }}>
                  <a href={r.link} style={{ color: '#111827', textDecoration: 'none' }}>
                    {r.name || `Partner #${r.partner_id}`}
                  </a>
                  {r.company ? <span style={{ fontWeight: 500, color: '#6b7280' }}> · {r.company}</span> : null}
                  {/* 🚨 WHICH NUMBER THIS ROW IS ABOUT. A broker of record has two
                      subjects and "verify the licence" is ambiguous between them. */}
                  {r.subject === 'account' && (
                    <span style={{ fontWeight: 600, color: '#6b7280' }}> &mdash; the firm&rsquo;s NMLS ID</span>
                  )}
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                  textTransform: 'uppercase', color: tone.fg }}>
                  {tone.label} · {age(r.age_days)}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: tone.fg, marginTop: 4, fontWeight: 700 }}>
                {KIND_LABEL[r.kind] || r.kind}
                {r.status_observed ? ` — “${r.status_observed}”` : ''}
                {r.registry ? ` · ${String(r.registry).toUpperCase()}` : ''}
              </div>
              <div style={{ fontSize: 12.5, color: '#374151', marginTop: 4, lineHeight: 1.55 }}>
                {r.what_to_do}
              </div>
              <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6 }}>
                {/* ⚠️ THE OWNER IS SHOWN ON EVERY ROW, including an admin's own —
                    an unowned partner is a gap only a person reading this can fix. */}
                {r.owner ? `Owner: ${r.owner.name || `#${r.owner.id}`}`
                  : 'Owner: none on record — needs assigning'}
                {' · '}
                <a href={r.link} style={{ color: '#4338ca' }}>Open the partner</a>
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
